import {
  classNames,
  create,
  createEventRegistrar,
  text,
} from "@hesxenon/prelude/Dom.js";
import { iife } from "@hesxenon/prelude/Function.js";
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  getWeekWithOptions,
  lastDayOfMonth,
  setDate,
  startOfDay,
  startOfMonth,
} from "date-fns/fp";
import { Events } from "../events";
import { getMonthName, getWeeDayNames } from "../format";
import { connectSelection } from "../parts/selection";
import type { Connect, View } from "../uwc-datepicker";

const connectGrid: Connect = (datepicker, opts) => {
  const on = createEventRegistrar(datepicker, opts.disconnect);

  const enWeekDayNames = getWeeDayNames(
    "en",
    datepicker["start-of-week"],
    "short",
  );
  const localizedWeekDayNames = getWeeDayNames(
    datepicker.locale,
    datepicker["start-of-week"],
    "short",
  );
  const localizedA11yWeekDayNames = getWeeDayNames(
    datepicker.locale,
    datepicker["start-of-week"],
    "long",
  );

  const createGrid = () => {
    const days = create("ul", { role: "grid" });
    days.append(
      create("li", {
        className: "placeholder",
      }),
    );

    localizedWeekDayNames.forEach((name, i) =>
      days.append(
        create(
          "li",
          {
            className: `weekday ${enWeekDayNames[i]!.toLowerCase()}`,
            title: localizedA11yWeekDayNames[i],
            ariaLabel: localizedA11yWeekDayNames[i],
          },
          [text(name)],
        ),
      ),
    );

    const now = new Date();

    const start = iife(() => {
      const firstOfMonth = setDate(1, datepicker.anchorDate);
      return startOfDay(
        // find the previous start of the week
        addDays(
          datepicker["start-of-week"] === "sunday"
            ? -firstOfMonth.getDay()
            : -((firstOfMonth.getDay() + 6) % 7), // add a full week - monday offset but limit to 7 days
          firstOfMonth,
        ),
      );
    });

    const end = iife(() => {
      const lastOfMonth = lastDayOfMonth(datepicker.anchorDate);
      return startOfDay(
        // find the next end of the week
        addDays(
          7 -
            (lastOfMonth.getDay() +
              (datepicker["start-of-week"] === "monday" ? 0 : 1)),
          lastOfMonth,
        ),
      );
    });

    let cursor = start;
    while (cursor <= end) {
      const next = addDays(1, cursor);

      if (
        cursor.getDay() === (datepicker["start-of-week"] === "monday" ? 1 : 0)
      ) {
        days.append(
          create(
            "li",
            {
              className: "weeknumber",
            },
            [
              text(
                getWeekWithOptions(
                  {
                    weekStartsOn:
                      datepicker["start-of-week"] === "monday" ? 1 : 0,
                  },
                  cursor,
                ),
              ),
            ],
          ),
        );
      }

      const dataValue = cursor.toISOString();
      days.append(
        create(
          "li",
          {
            "data-value": dataValue,
            className: classNames({
              "previous-month": cursor < startOfMonth(datepicker.anchorDate),
              "next-month": cursor > endOfMonth(datepicker.anchorDate),
              current: cursor <= now && now < next,
            }),
          },
          [text(cursor.getDate())],
        ),
      );
      cursor = next;
    }

    return days;
  };

  let grid = createGrid();

  on(Events["anchor-dateChanged"], () => {
    grid.remove();
    grid = createGrid();
    datepicker.append(grid);
  });

  datepicker.append(grid);
};

export const dayView: View = {
  addOneUnit: addDays(1),
  connect: (datepicker, opts) => {
    connectSelection(datepicker, {
      addPages: addMonths,
      leapPages: 12,
      getStartOfPage: startOfMonth,
      getLabel: (current, format) =>
        `${getMonthName(current, datepicker.locale, format)} ${current.getFullYear()}`,
      disconnect: opts.disconnect,
    });

    connectGrid(datepicker, opts);
  },
};
